import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { descritiva, testeTpareado, testeWilcoxon, pLabel, fmtNum } from '../lib/estatistica';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const CORES = ['#1e4fc2', '#0d9488', '#d97706', '#dc2626', '#7c3aed', '#059669'];

const RADIO_LABELS = {
  cobb_proximal: 'Cobb proximal', cobb_principal: 'Cobb principal',
  cobb_lombar: 'Cobb lombar', sva: 'SVA', csva: 'cSVA',
  lordose_cervical: 'Lordose cervical', lordose_lombar: 'Lordose lombar',
  cifose_t5_t12: 'Cifose T5-T12', deformidade_segmentar: 'Deformidade segmentar',
  slope_sacral: 'Slope sacral', pelvic_tilt: 'Pelvic tilt',
  incidencia_pelvica: 'Incidência pélvica', obliquidade_pelvica: 'Obliquidade pélvica'
};

// ── Componente: Tabela descritiva ─────────────────────────────────────────
function TabelaDescritiva({ dados, momentos, titulo }) {
  if (!dados || dados.length === 0) return null;
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><h2>{titulo}</h2></div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Momento</th>
              <th>N</th>
              <th>Média</th>
              <th>Mediana</th>
              <th>DP</th>
              <th>Mín</th>
              <th>Máx</th>
              <th>Q1</th>
              <th>Q3</th>
            </tr>
          </thead>
          <tbody>
            {dados.map((d, i) => (
              <tr key={i}>
                <td><strong>{d.label}</strong></td>
                <td>{d.est?.n ?? '—'}</td>
                <td>{fmtNum(d.est?.media)}</td>
                <td>{fmtNum(d.est?.mediana)}</td>
                <td>{fmtNum(d.est?.dp)}</td>
                <td>{fmtNum(d.est?.min)}</td>
                <td>{fmtNum(d.est?.max)}</td>
                <td>{fmtNum(d.est?.q1)}</td>
                <td>{fmtNum(d.est?.q3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Componente: Testes estatísticos pré vs pós ────────────────────────────
function TestesPrePos({ dadosPre, dadosPos, labelPre, labelPos, titulo }) {
  if (!dadosPre || !dadosPos) return null;
  const t = testeTpareado(dadosPre, dadosPos);
  const w = testeWilcoxon(dadosPre, dadosPos);
  if (!t && !w) return null;
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><h2>Testes {labelPre} vs {labelPos} — {titulo}</h2></div>
      <div className="card-body">
        <div className="grid-2" style={{ gap: 16 }}>
          {t && (
            <div style={{ padding: 16, background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div className="form-label">Teste t pareado</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', marginTop: 4 }}>
                t = {fmtNum(t.t, 3)}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
                df = {t.df} · n = {t.n}
              </div>
              <div style={{ marginTop: 8 }}>
                <span className={`badge ${t.p < 0.05 ? 'badge-teal' : 'badge-gray'}`} style={{ fontSize: 13 }}>
                  {pLabel(t.p)}
                </span>
              </div>
              <div className="text-sm text-muted" style={{ marginTop: 8 }}>
                Diferença média: {fmtNum(t.meanDiff)} (DP: {fmtNum(t.sdDiff)})
              </div>
            </div>
          )}
          {w && (
            <div style={{ padding: 16, background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div className="form-label">Wilcoxon signed-rank</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', marginTop: 4 }}>
                W = {fmtNum(w.W, 1)}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
                z = {fmtNum(w.z, 3)} · n = {w.n}
              </div>
              <div style={{ marginTop: 8 }}>
                <span className={`badge ${w.p < 0.05 ? 'badge-teal' : 'badge-gray'}`} style={{ fontSize: 13 }}>
                  {pLabel(w.p)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Componente: Gráfico de evolução temporal ──────────────────────────────
function GraficoEvolucao({ dados, titulo, unidade = '' }) {
  const chartData = dados.filter(d => d.est?.media !== undefined);
  if (chartData.length < 2) return null;
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><h2>Evolução temporal — {titulo}</h2></div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'var(--text-2)' }} />
            <YAxis tick={{ fontSize: 12, fill: 'var(--text-2)' }} />
            <Tooltip
              formatter={(v) => [`${fmtNum(v)}${unidade}`, 'Média']}
              contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            />
            <Line
              type="monotone" dataKey="est.media" stroke="#1e4fc2"
              strokeWidth={2.5} dot={{ r: 5, fill: '#1e4fc2' }}
              activeDot={{ r: 7 }} name="Média"
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="text-sm text-muted" style={{ textAlign: 'center', marginTop: 4 }}>
          Barras de erro representam ±1 DP
        </div>
      </div>
    </div>
  );
}

// ── Componente: Distribuição cirúrgica ────────────────────────────────────
function EstatisticasCirurgicas({ cirurgias }) {
  if (!cirurgias.length) return null;

  const tipos = {};
  const abordagens = {};
  const sangramento = cirurgias.map(c => c.sangramento_ml).filter(Boolean).map(Number);
  const tempo = cirurgias.map(c => c.tempo_cirurgico_min).filter(Boolean).map(Number);
  const niveis = cirurgias.map(c => c.numero_niveis).filter(Boolean).map(Number);

  cirurgias.forEach(c => {
    if (c.tipo_cirurgia) tipos[c.tipo_cirurgia] = (tipos[c.tipo_cirurgia] || 0) + 1;
    if (c.abordagem) abordagens[c.abordagem] = (abordagens[c.abordagem] || 0) + 1;
  });

  const estSang = descritiva(sangramento);
  const estTempo = descritiva(tempo);
  const estNiveis = descritiva(niveis);

  const tiposData = Object.entries(tipos).map(([name, value]) => ({ name, value }));
  const abordData = Object.entries(abordagens).map(([name, value]) => ({ name, value }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="grid-2" style={{ gap: 16 }}>
        {estSang && (
          <div className="card">
            <div className="card-header"><h2>Sangramento (ml)</h2></div>
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[['N', estSang.n],['Média', `${fmtNum(estSang.media)} ml`],['Mediana', `${fmtNum(estSang.mediana)} ml`],
                  ['DP', `${fmtNum(estSang.dp)} ml`],['Mín', `${fmtNum(estSang.min)} ml`],['Máx', `${fmtNum(estSang.max)} ml`]
                ].map(([l, v]) => (
                  <div key={l} className="flex-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <span className="text-sm text-muted">{l}</span>
                    <strong className="text-sm">{v}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {estTempo && (
          <div className="card">
            <div className="card-header"><h2>Tempo cirúrgico (min)</h2></div>
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[['N', estTempo.n],['Média', `${fmtNum(estTempo.media)} min`],['Mediana', `${fmtNum(estTempo.mediana)} min`],
                  ['DP', `${fmtNum(estTempo.dp)} min`],['Mín', `${fmtNum(estTempo.min)} min`],['Máx', `${fmtNum(estTempo.max)} min`]
                ].map(([l, v]) => (
                  <div key={l} className="flex-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <span className="text-sm text-muted">{l}</span>
                    <strong className="text-sm">{v}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {estNiveis && (
        <div className="card">
          <div className="card-header"><h2>Níveis operados</h2></div>
          <div className="card-body">
            <div className="grid-2">
              {[['N', estNiveis.n],['Média', fmtNum(estNiveis.media)],['Mediana', fmtNum(estNiveis.mediana)],
                ['DP', fmtNum(estNiveis.dp)],['Mín', fmtNum(estNiveis.min)],['Máx', fmtNum(estNiveis.max)]
              ].map(([l, v]) => (
                <div key={l} className="flex-between" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className="text-sm text-muted">{l}</span>
                  <strong className="text-sm">{v}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tiposData.length > 0 && (
        <div className="card">
          <div className="card-header"><h2>Tipos de cirurgia</h2></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={tiposData} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                <Bar dataKey="value" name="Pacientes" radius={[4,4,0,0]}>
                  {tiposData.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {abordData.length > 0 && (
        <div className="card">
          <div className="card-header"><h2>Abordagens cirúrgicas</h2></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={abordData} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                <Bar dataKey="value" name="Pacientes" radius={[4,4,0,0]}>
                  {abordData.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────
export default function EstatisticasPage() {
  const [pesquisas, setPesquisas] = useState([]);
  const [pesquisaSel, setPesquisaSel] = useState('');
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('scores');

  useEffect(() => {
    supabase.from('pesquisas').select('id, titulo').order('titulo')
      .then(({ data }) => setPesquisas(data || []));
  }, []);

  async function carregar() {
    if (!pesquisaSel) return;
    setLoading(true);
    setDados(null);

    const momentos = (await supabase.from('pesquisa_momentos').select('*').eq('pesquisa_id', pesquisaSel).order('ordem')).data || [];
    const paramRes = (await supabase.from('pesquisa_parametros_radio').select('*').eq('pesquisa_id', pesquisaSel).maybeSingle()).data || {};
    const scoresVinc = (await supabase.from('pesquisa_scores').select('score_id, scores(id, nome)').eq('pesquisa_id', pesquisaSel)).data || [];
    const scores = scoresVinc.map(s => s.scores).filter(Boolean);

    const ppList = (await supabase.from('pesquisa_pacientes').select('id, paciente_id').eq('pesquisa_id', pesquisaSel)).data || [];
    const ppIds = ppList.map(p => p.id);

    if (!ppIds.length) { setLoading(false); setDados({ vazio: true }); return; }

    const [avalRes, radioRes, cirRes] = await Promise.all([
      supabase.from('avaliacoes').select('*').in('pesquisa_paciente_id', ppIds),
      supabase.from('avaliacoes_radio').select('*').in('pesquisa_paciente_id', ppIds),
      supabase.from('cirurgias').select('*').in('pesquisa_paciente_id', ppIds),
    ]);

    const avaliacoes = avalRes.data || [];
    const radios = radioRes.data || [];
    const cirurgias = cirRes.data || [];

    // Calcular estatísticas por score e momento
    const estatScores = scores.map(score => {
      const porMomento = momentos.map(m => {
        const vals = avaliacoes
          .filter(a => a.score_id === score.id && a.momento_id === m.id)
          .map(a => a.pontuacao_total);
        return { label: m.label, momentoId: m.id, vals, est: descritiva(vals) };
      });
      return { score, porMomento };
    });

    // Calcular estatísticas por parâmetro radiográfico e momento
    const camposRadioAtivos = Object.entries(RADIO_LABELS).filter(([k]) => paramRes[k]);
    const extrasRadio = paramRes.extras || [];
    const todosRadio = [...camposRadioAtivos, ...extrasRadio.map(e => [e, e])];

    const estatRadio = todosRadio.map(([key, label]) => {
      const porMomento = momentos.map(m => {
        const vals = radios
          .filter(r => r.momento_id === m.id)
          .map(r => key.startsWith('extra_') ? r.extras?.[key] : r[key])
          .filter(v => v !== null && v !== undefined);
        return { label: m.label, momentoId: m.id, vals, est: descritiva(vals) };
      });
      return { key, label, porMomento };
    });

    setDados({ momentos, estatScores, estatRadio, cirurgias, nPacientes: ppList.length });
    setLoading(false);
  }

  const abas = [
    { id: 'scores', label: 'Scores clínicos' },
    { id: 'radio', label: 'Radiografia' },
    { id: 'cirurgia', label: 'Dados cirúrgicos' },
  ];

  return (
    <div>
      <style>{`
        .abas-nav { display: flex; gap: 4px; margin-bottom: 20px; }
        .aba-btn { padding: 8px 18px; border-radius: 8px; border: none; background: none; cursor: pointer; font-size: 14px; font-weight: 500; color: var(--text-2); transition: all .15s; font-family: var(--font); }
        .aba-btn:hover { background: var(--border); }
        .aba-btn.ativa { background: var(--navy); color: #fff; }
        .legenda-p { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 8px; font-size: 12px; color: var(--text-3); }
      `}</style>

      <div className="page-header">
        <div><h1>Estatísticas</h1><p>Análise descritiva e testes estatísticos por pesquisa</p></div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <div className="flex-row">
            <div style={{ flex: 1 }}>
              <label className="form-label">Selecione a pesquisa</label>
              <select className="form-select" value={pesquisaSel} onChange={e => setPesquisaSel(e.target.value)}>
                <option value="">Selecione...</option>
                {pesquisas.map(p => <option key={p.id} value={p.id}>{p.titulo}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={carregar} disabled={!pesquisaSel || loading} style={{ marginTop: 20 }}>
              {loading ? <><span className="spinner" />Calculando...</> : 'Calcular'}
            </button>
          </div>
        </div>
      </div>

      {dados?.vazio && (
        <div className="card"><div className="empty-state"><div className="empty-icon">📊</div><h3>Nenhum paciente nesta pesquisa</h3></div></div>
      )}

      {dados && !dados.vazio && (
        <>
          <div className="card" style={{ marginBottom: 20, padding: '12px 20px', background: 'var(--navy)', borderRadius: 12 }}>
            <div style={{ color: '#fff', fontSize: 14 }}>
              <strong>{dados.nPacientes}</strong> pacientes · <strong>{dados.momentos.length}</strong> momentos de avaliação
            </div>
          </div>

          <div className="abas-nav">
            {abas.map(a => (
              <button key={a.id} className={`aba-btn ${abaAtiva === a.id ? 'ativa' : ''}`} onClick={() => setAbaAtiva(a.id)}>
                {a.label}
              </button>
            ))}
          </div>

          {/* ABA: Scores */}
          {abaAtiva === 'scores' && dados.estatScores.map(({ score, porMomento }) => {
            const momComDados = porMomento.filter(m => m.est);
            if (!momComDados.length) return null;
            const preOp = porMomento.find(m => dados.momentos.find(mo => mo.id === m.momentoId)?.tipo === 'pre_op');
            const pos1 = porMomento.find(m => dados.momentos.find(mo => mo.id === m.momentoId)?.tipo === '3_meses');
            return (
              <div key={score.id}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--navy)', margin: '24px 0 12px' }}>{score.nome}</h3>
                <TabelaDescritiva dados={porMomento} titulo="Estatística descritiva" />
                <GraficoEvolucao dados={porMomento} titulo={score.nome}
                  unidade={score.nome.includes('ODI') || score.nome.includes('NDI') ? '%' : ' pts'} />
                {preOp && pos1 && (
                  <TestesPrePos
                    dadosPre={preOp.vals} dadosPos={pos1.vals}
                    labelPre={preOp.label} labelPos={pos1.label}
                    titulo={score.nome}
                  />
                )}
              </div>
            );
          })}

          {/* ABA: Radiografia */}
          {abaAtiva === 'radio' && dados.estatRadio.map(({ key, label, porMomento }) => {
            const momComDados = porMomento.filter(m => m.est);
            if (!momComDados.length) return null;
            const preOp = porMomento.find(m => dados.momentos.find(mo => mo.id === m.momentoId)?.tipo === 'pre_op');
            const pos1 = porMomento.find(m => dados.momentos.find(mo => mo.id === m.momentoId)?.tipo === '3_meses');
            return (
              <div key={key}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--navy)', margin: '24px 0 12px' }}>{label}</h3>
                <TabelaDescritiva dados={porMomento} titulo="Estatística descritiva" />
                <GraficoEvolucao dados={porMomento} titulo={label} unidade="°" />
                {preOp && pos1 && (
                  <TestesPrePos
                    dadosPre={preOp.vals} dadosPos={pos1.vals}
                    labelPre={preOp.label} labelPos={pos1.label}
                    titulo={label}
                  />
                )}
              </div>
            );
          })}

          {/* ABA: Cirurgia */}
          {abaAtiva === 'cirurgia' && <EstatisticasCirurgicas cirurgias={dados.cirurgias} />}

          <div className="legenda-p">
            <span>*** p &lt; 0,001</span><span>** p &lt; 0,01</span>
            <span>* p &lt; 0,05</span><span>ns = não significativo</span>
          </div>
        </>
      )}
    </div>
  );
}
